import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { useAppDispatch } from "./reduxHooks";
import { sendMessage } from "../ChatSlice_Store";

/** How long the thumb icon stays enlarged after a click. */
const SCALE_ANIMATION_MS = 300;
/** Give the feedback box a beat to render before scrolling to it. */
const SCROLL_DELAY_MS = 100;
/** Feedback control messages carry a short excerpt of the message text. */
const EXCERPT_LENGTH = 50;

type FeedbackType = "thumbs_up" | "thumbs_down";

interface ChatMessage {
  sender?: string;
  text?: string;
  turnId?: string;
}

export interface ChatFeedback {
  messageFeedback: Record<number, FeedbackType>;
  scalingMessages: Set<number>;
  textFeedback: Record<number, string>;
  showTextFeedback: Record<number, boolean>;
  latestBotMessageIndex: number;
  setTextFeedback: Dispatch<SetStateAction<Record<number, string>>>;
  hideTextFeedback: (messageIndex: number) => void;
  handleFeedback: (messageIndex: number, feedbackType: FeedbackType) => void;
  handleTextFeedbackSubmit: (messageIndex: number) => void;
}

interface FeedbackState {
  messageFeedback: Record<number, FeedbackType>;
  scalingMessages: Set<number>;
  textFeedback: Record<number, string>;
  showTextFeedback: Record<number, boolean>;
  latestBotMessageIndex: number;
  setMessageFeedback: Dispatch<SetStateAction<Record<number, FeedbackType>>>;
  setTextFeedback: Dispatch<SetStateAction<Record<number, string>>>;
  setShowTextFeedback: Dispatch<SetStateAction<Record<number, boolean>>>;
  setLatestBotMessageIndex: Dispatch<SetStateAction<number>>;
  pulseMessage: (messageIndex: number) => void;
}

/** Index of the last chatbot message, or -1 when there is none. */
function findLatestBotIndex(chatMessages: ChatMessage[]): number {
  for (let i = chatMessages.length - 1; i >= 0; i--) {
    if (chatMessages[i] && chatMessages[i].sender === "chatbot") {
      return i;
    }
  }
  return -1;
}

/** All per-message feedback state, plus the thumb pulse animation. */
function useFeedbackState(): FeedbackState {
  // Track feedback state for each message
  const [messageFeedback, setMessageFeedback] = useState<Record<number, FeedbackType>>({});
  // Track scaling animation state
  const [scalingMessages, setScalingMessages] = useState<Set<number>>(new Set());
  // Track text feedback for thumbs down
  const [textFeedback, setTextFeedback] = useState<Record<number, string>>({});
  const [showTextFeedback, setShowTextFeedback] = useState<Record<number, boolean>>({});
  // Track the latest bot message index to disable older feedback controls
  const [latestBotMessageIndex, setLatestBotMessageIndex] = useState<number>(-1);

  /** Briefly enlarge the clicked thumb. */
  const pulseMessage = (messageIndex: number): void => {
    setScalingMessages((prev) => new Set(prev).add(messageIndex));
    setTimeout(() => {
      setScalingMessages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(messageIndex);
        return newSet;
      });
    }, SCALE_ANIMATION_MS);
  };

  return {
    messageFeedback,
    scalingMessages,
    textFeedback,
    showTextFeedback,
    latestBotMessageIndex,
    setMessageFeedback,
    setTextFeedback,
    setShowTextFeedback,
    setLatestBotMessageIndex,
    pulseMessage,
  };
}

/** Clears the open feedback boxes whenever a newer bot message arrives. */
function useLatestBotMessage(chatMessages: ChatMessage[], state: FeedbackState): void {
  const { latestBotMessageIndex, setShowTextFeedback, setTextFeedback, setLatestBotMessageIndex } =
    state;

  useEffect(() => {
    const latestBotIndex = findLatestBotIndex(chatMessages);

    if (latestBotIndex > latestBotMessageIndex) {
      setShowTextFeedback({});
      setTextFeedback({});
      setLatestBotMessageIndex(latestBotIndex);
    }
  }, [chatMessages, latestBotMessageIndex]);
}

/** Thumbs up/down and free-text feedback on the latest bot message. */
export function useChatFeedback(
  chatMessages: ChatMessage[],
  scrollToBottom: () => void
): ChatFeedback {
  const dispatch = useAppDispatch();
  const state = useFeedbackState();
  useLatestBotMessage(chatMessages, state);

  const hideTextFeedback = (messageIndex: number): void => {
    state.setShowTextFeedback((prev) => ({ ...prev, [messageIndex]: false }));
  };

  /** Feedback control messages carry the turn id and a short excerpt. */
  const sendFeedback = (message: ChatMessage, feedbackType: FeedbackType, suffix = ""): void => {
    const excerpt = (message.text || "").substring(0, EXCERPT_LENGTH);
    void dispatch(
      sendMessage(`CONTROL_FEEDBACK:${feedbackType}:${message.turnId}:${excerpt}...${suffix}`)
    );
  };

  const handleFeedback = (messageIndex: number, feedbackType: FeedbackType): void => {
    const message = chatMessages[messageIndex];
    if (!message || message.sender !== "chatbot" || !message.turnId) return;

    state.setMessageFeedback((prev) => ({ ...prev, [messageIndex]: feedbackType }));
    state.pulseMessage(messageIndex);

    // Send immediate feedback for both thumbs up and thumbs down
    sendFeedback(message, feedbackType);

    // Show text feedback box for thumbs down (for optional additional comment)
    if (feedbackType === "thumbs_down") {
      state.setShowTextFeedback((prev) => ({ ...prev, [messageIndex]: true }));
      // Auto-scroll to bottom to show feedback section
      setTimeout(() => scrollToBottom(), SCROLL_DELAY_MS);
    } else {
      hideTextFeedback(messageIndex);
    }
  };

  const handleTextFeedbackSubmit = (messageIndex: number): void => {
    const message = chatMessages[messageIndex];
    if (!message || message.sender !== "chatbot" || !message.turnId) return;

    const textComment = state.textFeedback[messageIndex] || "";
    sendFeedback(message, "thumbs_down", `:TEXT:${textComment}`);

    // Clear the text feedback and hide the box
    state.setTextFeedback((prev) => ({ ...prev, [messageIndex]: "" }));
    hideTextFeedback(messageIndex);
  };

  return {
    messageFeedback: state.messageFeedback,
    scalingMessages: state.scalingMessages,
    textFeedback: state.textFeedback,
    showTextFeedback: state.showTextFeedback,
    latestBotMessageIndex: state.latestBotMessageIndex,
    setTextFeedback: state.setTextFeedback,
    hideTextFeedback,
    handleFeedback,
    handleTextFeedbackSubmit,
  };
}
